'use client';

import { c as _c } from "react/compiler-runtime";
import { useState, useEffect, useId } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash } from '@awesome.me/kit-94e593bf23/icons/sharp/light';
import { faCheck, faXmark } from '@awesome.me/kit-94e593bf23/icons/sharp/regular';
import { PASSWORD_MIN_LENGTH, PASSWORD_MAX_LENGTH, PASSWORD_HINT_TEXT } from '@/app/_lib/config/password';

/**
 * PasswordField - Reusable password input with real-time validation
 *
 * Features:
 * - Password input with show/hide toggle
 * - Optional confirm password field
 * - Real-time validation feedback (length, match)
 * - Password strength indicator
 * - Uses existing form-input styling patterns
 *
 * @param {string} name - Input name for password field
 * @param {string} confirmName - Input name for confirm field
 * @param {string} label - Label for password field
 * @param {string} confirmLabel - Label for confirm field
 * @param {boolean} showConfirm - Whether to show confirm password field
 * @param {string} defaultValue - Initial password value
 * @param {string} defaultConfirmValue - Initial confirm value
 * @param {object} serverErrors - Server-side validation errors { [name]: message }
 * @param {function} onChange - Callback with { password, confirmPassword }
 * @param {function} onValidChange - Callback with boolean isValid
 * @param {boolean} required - Whether fields are required
 * @param {string} className - Additional wrapper classes
 */
const PasswordField = t0 => {
  const $ = _c(98);
  const {
    name: t1,
    confirmName: t2,
    label: t3,
    confirmLabel: t4,
    showConfirm: t5,
    defaultValue: t6,
    defaultConfirmValue: t7,
    serverErrors: t8,
    onChange,
    onValidChange,
    required: t9,
    className: t10
  } = t0;
  const name = t1 === undefined ? "password_1" : t1;
  const confirmName = t2 === undefined ? "password_2" : t2;
  const label = t3 === undefined ? "Password" : t3;
  const confirmLabel = t4 === undefined ? "Confirm Password" : t4;
  const showConfirm = t5 === undefined ? true : t5;
  const defaultValue = t6 === undefined ? "" : t6;
  const defaultConfirmValue = t7 === undefined ? "" : t7;
  let t11;
  if ($[0] !== t8) {
    t11 = t8 === undefined ? {} : t8;
    $[0] = t8;
    $[1] = t11;
  } else {
    t11 = $[1];
  }
  const serverErrors = t11;
  const required = t9 === undefined ? true : t9;
  const className = t10 === undefined ? "" : t10;
  const uniqueId = useId();
  const passwordId = `${name}-${uniqueId}`;
  const confirmId = `${confirmName}-${uniqueId}`;
  const [password, setPassword] = useState(defaultValue);
  const [confirmPassword, setConfirmPassword] = useState(defaultConfirmValue);
  const [showPasswords, setShowPasswords] = useState(false);
  let t12;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = {
      password: false,
      confirm: false
    };
    $[2] = t12;
  } else {
    t12 = $[2];
  }
  const [touched, setTouched] = useState(t12);
  const isTooShort = password.length > 0 && password.length < PASSWORD_MIN_LENGTH;
  const isTooLong = password.length > PASSWORD_MAX_LENGTH;
  const passwordsMatch = !showConfirm || password === confirmPassword;
  const passwordsMismatch = showConfirm && touched.confirm && confirmPassword.length > 0 && !passwordsMatch;
  const isValid = password.length >= PASSWORD_MIN_LENGTH && !isTooLong && passwordsMatch;
  let t13;
  if ($[3] !== password.length) {
    const getStrength = () => {
      if (password.length === 0) {
        return null;
      }
      if (password.length < PASSWORD_MIN_LENGTH) {
        return {
          level: "weak",
          label: "Too short",
          percent: 25
        };
      }
      if (password.length < 20) {
        return {
          level: "fair",
          label: "Fair",
          percent: 50
        };
      }
      if (password.length < 30) {
        return {
          level: "good",
          label: "Good",
          percent: 75
        };
      }
      return {
        level: "strong",
        label: "Strong",
        percent: 100
      };
    };
    t13 = getStrength();
    $[3] = password.length;
    $[4] = t13;
  } else {
    t13 = $[4];
  }
  const strength = t13;
  let t14;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = {
      weak: "bg-red",
      fair: "bg-yellow-500",
      good: "bg-blue-500",
      strong: "bg-green-500"
    };
    $[5] = t14;
  } else {
    t14 = $[5];
  }
  const strengthColors = t14;
  let t15;
  let t16;
  if ($[6] !== confirmPassword || $[7] !== onChange || $[8] !== password) {
    t15 = () => {
      onChange?.({
        password,
        confirmPassword
      });
    };
    t16 = [password, confirmPassword, onChange];
    $[6] = confirmPassword;
    $[7] = onChange;
    $[8] = password;
    $[9] = t15;
    $[10] = t16;
  } else {
    t15 = $[9];
    t16 = $[10];
  }
  useEffect(t15, t16);
  let t17;
  let t18;
  if ($[11] !== isValid || $[12] !== onValidChange) {
    t17 = () => {
      onValidChange?.(isValid);
    };
    t18 = [isValid, onValidChange];
    $[11] = isValid;
    $[12] = onValidChange;
    $[13] = t17;
    $[14] = t18;
  } else {
    t17 = $[13];
    t18 = $[14];
  }
  useEffect(t17, t18);
  let t19;
  if ($[15] !== showPasswords) {
    t19 = () => {
      setShowPasswords(!showPasswords);
    };
    $[15] = showPasswords;
    $[16] = t19;
  } else {
    t19 = $[16];
  }
  const togglePasswordVisibility = t19;
  let t20;
  if ($[17] !== touched.password) {
    t20 = e => {
      setPassword(e.target.value);
      if (!touched.password) {
        setTouched(_temp);
      }
    };
    $[17] = touched.password;
    $[18] = t20;
  } else {
    t20 = $[18];
  }
  const handlePasswordChange = t20;
  let t21;
  if ($[19] !== touched.confirm) {
    t21 = e_0 => {
      setConfirmPassword(e_0.target.value);
      if (!touched.confirm) {
        setTouched(_temp2);
      }
    };
    $[19] = touched.confirm;
    $[20] = t21;
  } else {
    t21 = $[20];
  }
  const handleConfirmChange = t21;
  let t22;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = () => {
      setTouched(_temp3);
    };
    $[21] = t22;
  } else {
    t22 = $[21];
  }
  const handlePasswordBlur = t22;
  let t23;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = () => {
      setTouched(_temp4);
    };
    $[22] = t23;
  } else {
    t23 = $[22];
  }
  const handleConfirmBlur = t23;
  let t24;
  if ($[23] !== name || $[24] !== passwordId || $[25] !== serverErrors) {
    t24 = serverErrors[name] && <div className="text-red text-sm mb-1" id={`${passwordId}-error`}>{serverErrors[name]}</div>;
    $[23] = name;
    $[24] = passwordId;
    $[25] = serverErrors;
    $[26] = t24;
  } else {
    t24 = $[26];
  }
  const t25 = showPasswords ? "text" : "password";
  const t26 = required ? "true" : undefined;
  const t27 = !!serverErrors[name] || touched.password && isTooShort;
  const t28 = `${passwordId}-hint ${serverErrors[name] ? `${passwordId}-error` : ""}`;
  let t29;
  if ($[27] !== handlePasswordChange || $[28] !== name || $[29] !== password || $[30] !== passwordId || $[31] !== required || $[32] !== t25 || $[33] !== t26 || $[34] !== t27 || $[35] !== t28) {
    t29 = <input type={t25} id={passwordId} name={name} autoComplete="new-password" className="form-input peer pr-10" placeholder=" " value={password} onChange={handlePasswordChange} onBlur={handlePasswordBlur} required={required} minLength={PASSWORD_MIN_LENGTH} maxLength={PASSWORD_MAX_LENGTH} aria-required={t26} aria-invalid={t27} aria-describedby={t28} />;
    $[27] = handlePasswordChange;
    $[28] = name;
    $[29] = password;
    $[30] = passwordId;
    $[31] = required;
    $[32] = t25;
    $[33] = t26;
    $[34] = t27;
    $[35] = t28;
    $[36] = t29;
  } else {
    t29 = $[36];
  }
  let t30;
  if ($[37] !== required) {
    t30 = required && <span className="text-primary">*</span>;
    $[37] = required;
    $[38] = t30;
  } else {
    t30 = $[38];
  }
  let t31;
  if ($[39] !== label || $[40] !== passwordId || $[41] !== t30) {
    t31 = <label htmlFor={passwordId} className="form-input-label peer-placeholder-shown:form-input-label-placeholder-shown">{label} {t30}</label>;
    $[39] = label;
    $[40] = passwordId;
    $[41] = t30;
    $[42] = t31;
  } else {
    t31 = $[42];
  }
  const t32 = showPasswords ? "Hide passwords" : "Show passwords";
  const t33 = showPasswords ? "Hide passwords" : "Show passwords";
  const t34 = showPasswords ? faEye : faEyeSlash;
  let t35;
  if ($[43] !== t34) {
    t35 = <FontAwesomeIcon icon={t34} className="text-xs" aria-hidden="true" />;
    $[43] = t34;
    $[44] = t35;
  } else {
    t35 = $[44];
  }
  let t36;
  if ($[45] !== showPasswords || $[46] !== t32 || $[47] !== t33 || $[48] !== t35 || $[49] !== togglePasswordVisibility) {
    t36 = <button type="button" onClick={togglePasswordVisibility} tabIndex={-1} aria-label={t32} aria-pressed={showPasswords} title={t33} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1">{t35}</button>;
    $[45] = showPasswords;
    $[46] = t32;
    $[47] = t33;
    $[48] = t35;
    $[49] = togglePasswordVisibility;
    $[50] = t36;
  } else {
    t36 = $[50];
  }
  let t37;
  if ($[51] !== t29 || $[52] !== t31 || $[53] !== t36) {
    t37 = <div className="form-group">{t29}{t31}{t36}</div>;
    $[51] = t29;
    $[52] = t31;
    $[53] = t36;
    $[54] = t37;
  } else {
    t37 = $[54];
  }
  let t38;
  if ($[55] !== t24 || $[56] !== t37) {
    t38 = <div>{t24}{t37}</div>;
    $[55] = t24;
    $[56] = t37;
    $[57] = t38;
  } else {
    t38 = $[57];
  }
  let t39;
  if ($[58] !== confirmId || $[59] !== confirmLabel || $[60] !== confirmName || $[61] !== confirmPassword || $[62] !== handleConfirmChange || $[63] !== passwordsMismatch || $[64] !== required || $[65] !== serverErrors || $[66] !== showConfirm || $[67] !== showPasswords || $[68] !== togglePasswordVisibility) {
    t39 = showConfirm && <div>{serverErrors[confirmName] && <div className="text-red text-sm mb-1" id={`${confirmId}-error`}>{serverErrors[confirmName]}</div>}<div className="form-group"><input type={showPasswords ? "text" : "password"} id={confirmId} name={confirmName} autoComplete="new-password" className="form-input peer pr-10" placeholder=" " value={confirmPassword} onChange={handleConfirmChange} onBlur={handleConfirmBlur} required={required} minLength={PASSWORD_MIN_LENGTH} maxLength={PASSWORD_MAX_LENGTH} aria-required={required ? "true" : undefined} aria-invalid={!!serverErrors[confirmName] || passwordsMismatch} aria-describedby={serverErrors[confirmName] ? `${confirmId}-error` : undefined} /><label htmlFor={confirmId} className="form-input-label peer-placeholder-shown:form-input-label-placeholder-shown">{confirmLabel} {required && <span className="text-primary">*</span>}</label><button type="button" onClick={togglePasswordVisibility} tabIndex={-1} aria-label={showPasswords ? "Hide passwords" : "Show passwords"} aria-pressed={showPasswords} title={showPasswords ? "Hide passwords" : "Show passwords"} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1"><FontAwesomeIcon icon={showPasswords ? faEye : faEyeSlash} className="text-xs" aria-hidden="true" /></button></div></div>;
    $[58] = confirmId;
    $[59] = confirmLabel;
    $[60] = confirmName;
    $[61] = confirmPassword;
    $[62] = handleConfirmChange;
    $[63] = passwordsMismatch;
    $[64] = required;
    $[65] = serverErrors;
    $[66] = showConfirm;
    $[67] = showPasswords;
    $[68] = togglePasswordVisibility;
    $[69] = t39;
  } else {
    t39 = $[69];
  }
  let t40;
  if ($[70] !== t38 || $[71] !== t39) {
    t40 = <div className="grid grid-cols-1 gap-4 mb-2">{t38}{t39}</div>;
    $[70] = t38;
    $[71] = t39;
    $[72] = t40;
  } else {
    t40 = $[72];
  }
  const t41 = `${passwordId}-hint`;
  let t42;
  if ($[73] !== t41) {
    t42 = <p id={t41} className="text-2xs text-light">{PASSWORD_HINT_TEXT}</p>;
    $[73] = t41;
    $[74] = t42;
  } else {
    t42 = $[74];
  }
  let t43;
  if ($[75] !== strength) {
    t43 = strength && <div className="flex items-center gap-2"><div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden"><div className={`h-full transition-all duration-300 ${strengthColors[strength.level]}`} style={{
          width: `${strength.percent}%`
        }} /></div><span className={`text-2xs font-medium ${strength.level === "weak" ? "text-red" : strength.level === "fair" ? "text-yellow-600" : strength.level === "good" ? "text-blue-600" : "text-green-600"}`}>{strength.label}</span></div>;
    $[75] = strength;
    $[76] = t43;
  } else {
    t43 = $[76];
  }
  let t44;
  if ($[77] !== isTooShort || $[78] !== touched.password) {
    t44 = touched.password && isTooShort && <div className="flex items-center gap-1.5 text-2xs text-red"><FontAwesomeIcon icon={faXmark} className="text-xs" /><span>Password must be at least {PASSWORD_MIN_LENGTH} characters</span></div>;
    $[77] = isTooShort;
    $[78] = touched.password;
    $[79] = t44;
  } else {
    t44 = $[79];
  }
  let t45;
  if ($[80] !== passwordsMismatch) {
    t45 = passwordsMismatch && <div className="flex items-center gap-1.5 text-2xs text-red"><FontAwesomeIcon icon={faXmark} className="text-xs" /><span>Passwords do not match</span></div>;
    $[80] = passwordsMismatch;
    $[81] = t45;
  } else {
    t45 = $[81];
  }
  let t46;
  if ($[82] !== confirmPassword || $[83] !== password.length || $[84] !== passwordsMatch || $[85] !== showConfirm || $[86] !== touched.confirm) {
    t46 = showConfirm && touched.confirm && passwordsMatch && password.length >= PASSWORD_MIN_LENGTH && confirmPassword.length > 0 && <div className="flex items-center gap-1.5 text-2xs text-green-600"><FontAwesomeIcon icon={faCheck} className="text-xs" /><span>Passwords match</span></div>;
    $[82] = confirmPassword;
    $[83] = password.length;
    $[84] = passwordsMatch;
    $[85] = showConfirm;
    $[86] = touched.confirm;
    $[87] = t46;
  } else {
    t46 = $[87];
  }
  let t47;
  if ($[88] !== t42 || $[89] !== t43 || $[90] !== t44 || $[91] !== t45 || $[92] !== t46) {
    t47 = <div className="space-y-2">{t42}{t43}{t44}{t45}{t46}</div>;
    $[88] = t42;
    $[89] = t43;
    $[90] = t44;
    $[91] = t45;
    $[92] = t46;
    $[93] = t47;
  } else {
    t47 = $[93];
  }
  let t48;
  if ($[94] !== className || $[95] !== t40 || $[96] !== t47) {
    t48 = <div className={className}>{t40}{t47}</div>;
    $[94] = className;
    $[95] = t40;
    $[96] = t47;
    $[97] = t48;
  } else {
    t48 = $[97];
  }
  return t48;
};
export default PasswordField;
function _temp(prev) {
  return {
    ...prev,
    password: true
  };
}
function _temp2(prev_0) {
  return {
    ...prev_0,
    confirm: true
  };
}
function _temp3(prev_1) {
  return {
    ...prev_1,
    password: true
  };
}
function _temp4(prev_2) {
  return {
    ...prev_2,
    confirm: true
  };
}